// The client page's "סנכרון CallMarker" button (POST /call-marker/customers/:user_id/sync, admin only).
// Pure helpers, no imports, so tests/callMarkerSync.check.ts can run them directly.
// Success is never inferred from HTTP 200: only the server's `status` and the card / initial Purchased checks count.

export type CallMarkerSyncMessage = { type: 'success' | 'warning' | 'error' | 'info'; text: string }

// Only the admin role; developer, moderator, editor, money, agent and viewer never see it (the server enforces it too).
export const canSyncCallMarker = (roles: any): boolean => Array.isArray(roles) && roles.indexOf('admin') !== -1

// A strict positive client ID from the route (digits only, no leading zero), else null.
export const callMarkerSyncUserId = (id: any): string | null =>
    typeof id === 'string' && /^[1-9]\d{0,9}$/.test(id) ? id : null

const CHECK = 'יש לבדוק ב-CallMarker ולא לנסות שוב בלי בדיקה.'

// A short server reason code (letters, digits, underscores) or nothing; never free text.
const code = (reason: any): string =>
    typeof reason === 'string' && /^[A-Z0-9_]{1,64}$/.test(reason) ? ` (${reason})` : ''

const SKIP_TEXT: { [reason: string]: string } = {
    USER_DELETED: 'המשתמש מחוק, לא סונכרן.',
    FAMILY_PROFILE_PENDING_DECISION: 'בן משפחה אינו מסונכרן ככרטיס נפרד.',
    NO_SENDABLE_FIELDS: 'אין נתונים לשליחה.'
}

// `status`: the HTTP status, or null when no response arrived (the request may still have run on the server).
export function callMarkerSyncMessage(status: number | null, body: any): CallMarkerSyncMessage {
    const b = body && typeof body === 'object' ? body : {}
    if (status === null) return { type: 'error', text: `לא התקבלה תשובה מהשרת. ייתכן שהסנכרון בוצע: ${CHECK}` }
    if (status === 403) return { type: 'error', text: 'אין הרשאה לסנכרון CallMarker.' }
    if (status === 404) return { type: 'error', text: 'הלקוח לא נמצא.' }
    if (status === 400) return { type: 'error', text: 'מזהה לקוח לא תקין.' }
    if (b.status === 'disabled') return { type: 'info', text: 'סנכרון CallMarker מושבת בסביבה זו. לא נשלח דבר.' }
    if (status === 200 && b.status === 'skipped')
        return { type: 'info', text: `סנכרון CallMarker לא בוצע: ${SKIP_TEXT[b.reason] || 'דולג'}${code(b.reason)}` }
    if (status === 200 && b.status === 'confirmed') {
        const card = b.card && b.card.confirmed === true
        const purchase = b.initial_purchase && b.initial_purchase.status
        if (card && purchase === 'created')
            return {
                type: 'success',
                text: 'הכרטיס ב-CallMarker סונכרן ואומת. נוצרה הזדמנות Purchased ראשונית ואומתה.'
            }
        if (card && purchase === 'not_needed')
            return { type: 'success', text: 'הכרטיס ב-CallMarker סונכרן ואומת. לא נדרשה הזדמנות Purchased.' }
    }
    if (status === 200 && b.status === 'partial') {
        const p = b.initial_purchase || {}
        const reason = b.card && b.card.confirmed !== true ? b.card.reason : p.reason
        return { type: 'warning', text: `הסנכרון בוצע חלקית או לא אומת${code(reason)}. ${CHECK}` }
    }
    if (b.status === 'failed') return { type: 'error', text: `סנכרון CallMarker נכשל${code(b.reason)}. ${CHECK}` }
    // Any other answer (an unknown status, a 200 without the expected checks, a 5xx): never success.
    return { type: 'warning', text: `תוצאת הסנכרון לא ידועה. ${CHECK}` }
}
